import { AcknowledgementGroupConfig, AcknowledgementPointConfig } from '@/types/subscriptionAgreement'

export const ACKNOWLEDGEMENT_GROUPS: AcknowledgementGroupConfig[] = [
  { id: 'A', label: 'What you pay' },
  { id: 'B', label: 'Adding, removing, and cancelling' },
  { id: 'C', label: 'Providers and scheduling' },
  { id: 'D', label: 'Risk, responsibility, and data' },
]

export const ACKNOWLEDGEMENT_POINTS: AcknowledgementPointConfig[] = [
  {
    id: 'point-1',
    group: 'A',
    number: 1,
    title: '$50 per user, per month — monthly billing only',
    body: [
      "Every user account costs $50/month at the standard rate, whether that person is a provider or an office user. Job title and hours worked don't change the price. Billing is monthly only — there is no annual or prepaid plan — and it renews automatically on the same date each month until I cancel.",
    ],
    ref: 'Agreement §2.1, §2.2, §3.1, Exhibit A',
  },
  {
    id: 'point-2',
    group: 'A',
    number: 2,
    title: 'system fee on everything collected — including patient payments',
    body: [
      'PillarThera deducts a Per-Claim System Fee at the standard rate from every payment settled through the platform. This applies to insurance reimbursements and to money received directly from patients — copays, coinsurance, deductibles, and private-pay balances. It is calculated on the gross amount before provider pay or any other deduction.',
      "Stripe's own processing fees are charged separately by Stripe and are additional to this fee.",
    ],
    ref: 'Agreement §6.1–6.4, Exhibit A',
  },
  {
    id: 'point-3',
    group: 'A',
    number: 3,
    title: 'Discount codes are temporary — rates go back to standard when they end',
    body: [
      'A discount code may waive months, reduce the per-user fee, or lower the system fee rate. Each code carries its own terms — how much, how long, and when it ends — shown before it’s applied.',
      'When a discount expires or runs out, the standard per-user rate and system fee resume automatically at the next billing date, with no further notice. Only one code can be active at a time, codes don’t stack, and they aren’t applied to charges already processed.',
    ],
    ref: 'Agreement §4.5, §6.1, Exhibit A',
  },
  {
    id: 'point-4',
    group: 'B',
    number: 4,
    title: 'Mid-month changes are pro-rated by the day',
    body: [
      'I can add or deactivate users any time without asking PillarThera. Adding a user mid-month charges only the remaining days, then that seat joins the common billing date. Deactivating a user stops the charge at the end of that day and credits the unused days.',
      'Both "temporarily deactivated" and "removed" stop billing the same way — the difference is whether the account can be reactivated later, not the cost.',
    ],
    ref: 'Agreement §5.1–5.3, §5.5, Exhibit B',
  },
  {
    id: 'point-5',
    group: 'B',
    number: 5,
    title: 'Unused days come back as account credit, not a refund',
    body: [
      "Credits from mid-month deactivations apply automatically against my next invoice rather than returning to my card. Any unused credit is refunded when I close the account.",
      'If PillarThera suspends a user for a policy or security violation, no credit is issued for the rest of that month — though it is credited retroactively if the review clears us.',
    ],
    ref: 'Agreement §5.4, §5.6, §5.7',
  },
  {
    id: 'point-6',
    group: 'B',
    number: 6,
    title: "Cancelling ends the month I've paid for — then read-only access",
    body: [
      'I can cancel any time; it takes effect at the end of the current billing month, and that month is not refunded. Afterward I keep free read-only access to Claims and Invoices so I can follow payments that arrive later — for up to 12 months, or until outstanding claims resolve.',
      'During that period I can view and export payment status but cannot schedule, document, or submit claims, and the system fee still applies to money that settles.',
    ],
    ref: 'Agreement §2.3, §15.4',
  },
  {
    id: 'point-7',
    group: 'C',
    number: 7,
    title: 'A signed Agency–Provider Agreement is required — template or my own',
    body: [
      "Affiliation isn't active until my agency and the provider have a signed agreement in place. PillarThera supplies a template and fills in both parties' details so we can move forward without waiting on separate drafting. It is a convenience, not legal advice, and my own counsel should review it.",
      'Using the template is optional. If my counsel prefers our own form, I can execute our own agreement offline and upload the signed copy to the Document Center for record-keeping. Either route satisfies the requirement.',
      "The service rates and compensation go into that agreement and are agreed between my agency and the provider alone. PillarThera doesn't set, approve, verify, or enforce them.",
    ],
    ref: 'Agreement §9.1–9.3, §9.2.4',
  },
  {
    id: 'point-8',
    group: 'C',
    number: 8,
    title: "Scheduling follows each provider's own availability",
    body: [
      'Appointments can only be booked into the availability a provider has published, which prevents double-booking. Where a provider works with more than one agency, that availability is shared across all of them — a booking by another agency removes the slot for mine.',
      "PillarThera doesn't guarantee any provider will publish availability, accept work, or attend a visit. Coverage, missed visits, and continuity of care remain my agency's responsibility.",
    ],
    ref: 'Agreement §3.5, §10.1–10.4',
  },
  {
    id: 'point-9',
    group: 'D',
    number: 9,
    title: 'No guarantee of patients, providers, or revenue',
    body: [
      'PillarThera provides software. It does not promise any patients, referrals, provider affiliations, claim approvals, or level of revenue, and my subscription fees are owed regardless of how much business the platform brings in.',
      'My agency remains responsible for clinical decisions, billing and coding accuracy, licensure and credentialing, employment classification of our workers, and compliance with applicable law.',
    ],
    ref: 'Agreement §11, §12, §9.4, §9.5',
  },
  {
    id: 'point-10',
    group: 'D',
    number: 10,
    title: 'Data retention, export, and de-identified data',
    body: [
      'Records stay fully available in the platform for 7 years, then move to a Cold Archive that requires a written retrieval request and fee. After termination I have 90 days to export everything. Meeting my own legal retention obligations is still my agency’s responsibility.',
      'PillarThera may use de-identified, aggregated data — stripped of patient identifiers under HIPAA standards — to improve the platform and train its AI features. It will not re-identify that data or publish anything that identifies my agency by name.',
    ],
    ref: 'Agreement §13, §14',
  },
]
